<template>
  <ContentContainer :loadingStatus="isLoading">
    <div class="rate-code-list content-wrapper">
      <div class="page-header">{{ $t('rateCode') }}
        <popover-preview-remark-component
          :hotel-id="activeHotelId"
          :remark-type="REMARK_TYPE.RATE_CODE">
        </popover-preview-remark-component>
      </div>
      <div>
        <el-row>
          <el-col :span="4">
            <el-input
              :placeholder="$t('keyword')"
              v-model="keyword"
              @input="isSearching = true"></el-input>
          </el-col>
          <el-col :span="20">
            <router-link :to="onGotoAddButtonClick()" style="float: right">
              <el-button type="primary" icon="el-icon-plus"> {{ $t('newRateCode') }}</el-button>
            </router-link>
          </el-col>
        </el-row>
        <div style="margin-top: 25px">
          <el-table
            id="rate-code-list-table"
            :data="rateCodeListDataSource"
            :row-class-name="getRowClassName"
            :default-sort = "{prop: 'code', order: 'ascending'}"
            v-loading="isSearching"
            style="width: 100%"
            header-row-class-name="table-header-color">
            <el-table-column
              :label="$t('default')"
              width="90px">
              <template slot-scope="scope">
                <el-checkbox v-model="isDefaultList[scope.row.id]" :label="scope.row.id" @change="onChangeIsDefault(scope.row.id)"> &nbsp; </el-checkbox>
              </template>
            </el-table-column>
            <el-table-column
              prop="code"
              :label="$t('code')">
            </el-table-column>
            <el-table-column
              :label="$t('name')">
              <template slot-scope="scope">{{ scope.row.name }}</template>
            </el-table-column>
            <el-table-column>
              <template slot="header">
                <label v-html="$t('totalPeriod')"></label>
              </template>
              <template slot-scope="scope">{{ scope.row.totalPeriod }}</template>
            </el-table-column>
            <el-table-column>
              <template slot="header">
                <label v-html="$t('totalRoom')"></label>
              </template>
              <template slot-scope="scope">{{ scope.row.totalRoom }}</template>
            </el-table-column>
            <el-table-column>
              <template slot="header">
                <label v-html="$t('includeBreakfast')"></label>
              </template>
              <template slot-scope="scope">
                  <span v-if="scope.row.includeBreakfast === 1">{{ $t('yes') }}</span>
                  <span v-if="scope.row.includeBreakfast === 0 || scope.row.includeBreakfast === undefined">{{ $t('no') }}</span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('status')">
              <template slot-scope="scope">
                <el-tag
                  :type="scope.row.isEnable === 1 ? 'success' : 'danger'" size="mini"
                  disable-transitions>{{ getRateCodeStatus(scope.row.isEnable) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column width="135">
              <template slot="header">
                <label v-html="$t('lastUpdate')"></label>
              </template>
              <template slot-scope="scope">{{ dateFormat.long(new Date(scope.row.updatedAt)) }}</template>
            </el-table-column>
            <el-table-column width="100">
              <template slot-scope="scope">
                <view-rate-detail-button
                  :activeHotelId="activeHotelId"
                  :rateCode="scope.row"
                  :roomTypeList="roomTypeList"
                  @onGetDetailError="onGetDetailError"
                ></view-rate-detail-button>
              </template>
            </el-table-column>
            <el-table-column align="center" width="100">
              <template slot-scope="scope">
                <el-button type="primary" size="small" icon="el-icon-edit" plain circle @click="onGotoEditButtonClick(scope.row)"></el-button>
                <el-button type="danger" size="small" icon="el-icon-delete" plain circle @click="onRemoveRateCodeButtonClick(scope.row)"></el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </ContentContainer>
</template>

<script>
import i18nMessages from '@/translations/Hotel/ManageHotel/RateCode/List.i18n'
import AlertViewMixin from '@/mixins/alert-view-mixin'
import LoadingViewMixin from '@/mixins/loading-view-mixin'
import NotificationMixin from '@/mixins/notification-mixin'
import HotelRateCodeApi from '@/api/hotel/rate-code-api'
import HotelRoomTypeApi from '@/api/hotel/room-type-api'
import ViewRateDetailButton from '@/components/Button/ViewRateDetailButton'
import ContentContainer from '@/components/ContentContainerComponent.vue'
import PopoverPreviewRemarkComponent from '../../components/PopoverPreviewRemarkComponent'
import LocalStorageFactory from '@/factories/local-storage-factory'
import { getHotelIndexRoute } from '@/services/hotel-service'
import { filterDataByKeyword } from '@/services/general-service'
import { getStatusName } from '@/helpers'
import { mapState } from 'vuex'
import { REMARK_TYPE } from '@/configs/constant'
import { get, debounce } from 'lodash'
import { dateFormat } from '@/helpers/datetime'

export default {
  name: 'HotelRateCodeList',
  mixins: [AlertViewMixin, LoadingViewMixin, NotificationMixin],
  components: { ContentContainer, ViewRateDetailButton, PopoverPreviewRemarkComponent },
  i18n: { messages: i18nMessages },
  created: async function () {
    if (this.activeHotelId === undefined || this.activeHotelId === '') {
      const companyDetail = LocalStorageFactory.getCompanyDetail()
      const routeName = await getHotelIndexRoute(companyDetail, this.$store)

      return this.$router.push({
        name: routeName
      })
    }

    try {
      this.isLoading = true
      this.rateCodeList = await HotelRateCodeApi.list(this.activeHotelId)
      this.roomTypeList = await HotelRoomTypeApi.list(this.activeHotelId)
      this.isDefaultList = this._prepareIsDefaultList()
      this.getRateCodeListDataSource()
    } catch (err) {
      this.alertError(err.message)
    }

    this.isLoading = false
  },
  computed: {
    ...mapState({
      activeHotelId: state => state.general.activeHotelId
    })
  },
  data () {
    return {
      dateFormat,
      REMARK_TYPE,
      isLoading: false,
      isVisibleRateDetail: false,
      keyword: '',
      isSearching: false,
      roomTypeList: [],
      rateCodeList: [],
      isDefaultList: {},
      rateCodeListDataSource: []
    }
  },
  watch: {
    keyword: debounce(function () {
      this.isSearching = false
    }, 300),
    isSearching: function (value) {
      if (!value) {
        this.getRateCodeListDataSource()
      }
    }
  },
  methods: {
    onChangeIsDefault: async function (id) {
      try {
        this.startLoading()

        const isDefault = (this.isDefaultList[id] === true) ? 1 : 0
        await HotelRateCodeApi.updateIsDefault(this.activeHotelId, id, isDefault)

        const notificationTitle = this.$t('updateIsDefaultRateCodeSuccess')
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    getRateCodeListDataSource: function () {
      const searchKeys = ['code', 'name']
      const filteredRateCodeList = filterDataByKeyword(this.rateCodeList, this.keyword, searchKeys)
      this.rateCodeListDataSource = [...filteredRateCodeList]
    },
    getRateCodeStatus: function (status) {
      return this.$t(getStatusName(status))
    },
    onGotoAddButtonClick: function () {
      return {
        name: 'HotelRateCodeAdd',
        params: {
          roomTypeList: this.roomTypeList
        }
      }
    },
    onGotoEditButtonClick: function (rateCode) {
      this.$router.push({
        name: 'HotelRateCodeEdit',
        params: {
          rateCode: rateCode,
          roomTypeList: this.roomTypeList
        }
      })
    },
    onRemoveRateCodeButtonClick: async function (row) {
      const confirmMessage = this.$t('confirmRemoveRateCodeMessage', {
        rateCodeName: row.name,
        rateCode: row.code
      })
      const confirmButtonText = this.$t('confirmButtonText')
      const cancelButtonText = this.$t('cancelButtonText')
      const isConfirmRemove = await this.alertConfirmDelete(confirmMessage, confirmButtonText, cancelButtonText)

      if (!isConfirmRemove) {
        return false
      }

      this.removeRateCode(row.id)
    },
    removeRateCode: async function (rateCodeId) {
      try {
        this.startLoading()

        await HotelRateCodeApi.remove(this.activeHotelId, rateCodeId)
        this.rateCodeList = this.rateCodeList.filter(item => item.id !== rateCodeId)
        this.getRateCodeListDataSource()
        const notificationTitle = this.$t('removeRateCodeSuccess')
        this.showNotificationSuccess(notificationTitle)
      } catch (err) {
        this.alertError(err.message)
      }

      this.stopLoading()
    },
    onGetDetailError: function (err) {
      this.alertError(err.message)
    },
    getRowClassName: function ({ row, rowIndex }) {
      if (row.isEnable === 1) {
        return ''
      }
      return 'disable-row'
    },
    _prepareIsDefaultList: function () {
      const isDefaultList = {}

      this.rateCodeList.forEach((rateCode) => {
        const rateCodeId = rateCode.id
        isDefaultList[rateCodeId] = Boolean(get(rateCode, 'isDefault', false))
      })

      return isDefaultList
    }
  }
}
</script>

<style scoped>
  #rate-code-list-table {
    width: 100%;
    border: 1px solid #dddddd;
    border-radius: 5px;
    box-shadow: 0px 3px 5px 1px #eeeeee;
  }
  .el-table th {
    background-color: #eeeeee;
  }
  .el-form > div {
    text-align: left;
  }
</style>
